import * as React from 'react';

import once from 'lodash/once';
import {FormikProps} from 'formik';
import PaperClipOutlined from '@ant-design/icons/PaperClipOutlined';
import message from 'antd/lib/message';
import Tag from 'antd/lib/tag';
import Button from 'antd/lib/button';

import {Chat, ChatMessage, ChatMessageType} from '@common/react/objects/Chat';
import {ButtonsProps, ChatFormComponentState, NewMessage} from '@common/react/components/Chat/ChatMessageForm';
import VideoMessageInput from '@common/react/components/UI/VideoMessage/VideoMessageInput';
import VoiceMessageRecorder from '@common/react/components/UI/VoiceMessage/VoiceMessageRecorder';
import EmojiComponent from '@common/react/components/Chat/EmojiComponent';
import {FileType} from '@common/typescript/objects/FileInterface';
import File from '@common/react/components/Forms/Files/File';
import TypingComponent from '@common/react/components/Chat/TypingComponent';
import MentionsWithTyping, {CustomMentionProps} from '@common/react/components/Chat/MentionsWithTyping';
import VideoChatButton from '@common/react/components/UI/VideoChat/VideoChatButton';
import {getChatMessageMarkup, transformMentionMessage} from '@common/react/components/Chat/ChatUtils';
import {getFilesLayout} from '@common/react/components/Chat/ChatMessageList';
import VoiceMessageChatPlayer from '@common/react/components/UI/VoiceMessage/VoiceMessageChatPlayer';
import VideoMessageChatPlayer from '@common/react/components/UI/VideoMessage/VideoMessageChatPlayer';
import {getUserPick} from '@common/react/components/Chat/ChatListItem';
import {getAvatar} from '@common/react/components/Utils/Utils';
import * as ChatsState from '@common/react/store/Chats';
import {List} from '@common/typescript/objects/List';
import Colorbox from '@common/react/components/UI/Colorbox/Colorbox';
import SystemCallButton from '@common/react/components/UI/VideoChat/SystemCallButton';

import LinkPreviewComponent, {linkRegex} from '@common/react/components/UI/LinkPreview/LinkPreview';
import {LinkPreviewContentType} from '@common/react/objects/LinkPreview';
import { BaseParams } from '@common/react/objects/BaseParams';
import { phoneFormat } from '@common/react/components/Forms/FormikPhoneControl/FormikPhoneControl';
import { ChatListHeaderSettingsType } from '@common/react/components/Chat/ChatsListHeaderComponents';
import {
	ChatMessagesHeaderSettingsType,
	ChatNameType
} from '@common/react/components/Chat/ChatMessagesHeaderComponents';
import RegularChatMessage from '@common/react/components/Chat/RegularChatMessage';
import LinkPreviewGroup from '@common/react/components/UI/LinkPreview/LinkPreviewGroup';

export interface ChatSettingsRequests {
	chat: string;
	chatUser: string;
	chatMessage: string;
	chatMessageAccess: string;
	typing: string;
	completeChat?: string;
	removeMessage?: string;
	updateMessage?: string;
}

export interface ChatSettingsNotificationTypes {
	chat: string;
	chatUser: string;
	chatMessage: string;
	chatMessageAccess: string;
	typing: string;
}

export interface ChatSettingsUserSettings {
	useBrowserNotification: boolean;
	sound: boolean;
}

export interface ChatSettingsAvatarSettings {
	// maybe better return components with special interface
	getUserAvatar: (user: any) => React.ReactNode;
	getChatAvatar: (chat: any, userId: number) => React.ReactNode;
}

export interface ChatSettingsFormSettings {
	sendByEnter: boolean;
	underFormLabel: string;
	allowPasteImages: boolean;
	maxAttachmentsCount: number;
	maxMessageLength: number;
	waveColor?: string;
}

export enum ChatFormButtons {
	File,
	Emoji,
	Voice,
	Video
}

interface ChatFormButtonsProps extends ButtonsProps {
	isEditMessage?: boolean;
	waveColor?: string;
	setState: React.Dispatch<React.SetStateAction<ChatFormComponentState>>;
}

export const ChatFormButtonsDictionary = {
	[ChatFormButtons.File]: (formikBag: FormikProps<NewMessage>, props: ChatFormButtonsProps) => <React.Fragment key="file">
		<File
			multiple
			buttonCaption={<PaperClipOutlined style={{ fontSize: 16 }} />}
			asButton
			objectId={-1}
			type="chatMessage"
			update={props.onUploadFile}
			onError={(err: any) => {
				message.error(typeof err === 'object' ? err.message : err);
			}}
			fileType={FileType.File}
			buttonClassName="btn btn-sm btn-default chat-message-item"
			showError={false}
		/>
	</React.Fragment>,
	[ChatFormButtons.Emoji]: (formikBag: FormikProps<NewMessage>, props: ChatFormButtonsProps) => <React.Fragment key="emoji">
		<EmojiComponent
			onSelect={emoji => formikBag.setFieldValue('text', formikBag.values.text.concat(emoji.native))}
			triggerNode={
				<button
					type="button"
					className="btn btn-sm btn-default"
					style={{fontSize: 16, lineHeight: '20px'}}
				>
					<i className="fa fa-smile-o fa chat-message-item-ptr" aria-hidden="true" />
				</button>
			}
		/>
	</React.Fragment>,
	[ChatFormButtons.Voice]: (formikBag: FormikProps<NewMessage>, props: ChatFormButtonsProps) => <React.Fragment key="voice">
		<VoiceMessageRecorder
			disabled={props.isEditMessage}
			shape="default"
			waveColor={props.waveColor}
			className="btn-default"
			recordAudioResult={undefined}
			setRecordAudioResult={recordAudioResult => props.setState(prev => ({...prev, recordAudioResult}))}
			onChange={(value) => {
				props.setState(prev => ({...prev, recordVideoResult: undefined}));

				if (!value) {
					formikBag.setFieldValue('bytes', []);
					return;
				}

				value?.blob.arrayBuffer()
					.then((buffer) => {
						const byteArray = new Uint8Array(buffer);
						formikBag.setFieldValue('bytes', Array.from(byteArray));
						formikBag.setFieldValue('chatMessageType', ChatMessageType.VoiceMessage);
					});
			}}
		/>
	</React.Fragment>,
	[ChatFormButtons.Video]: (formikBag: FormikProps<NewMessage>, props: ChatFormButtonsProps) => <React.Fragment key="video">
		<VideoMessageInput
			disabled={props.isEditMessage}
			waveColor={props.waveColor}
			className="btn-default ml10"
			containerClassName=" "
			setRecordVideoResult={recordVideoResult => props.setState(prev => ({...prev, recordVideoResult}))}
			onStop={(value) => {
				props.setState(prev => ({...prev, recordAudioResult: undefined}));

				if (!value) {
					formikBag.setFieldValue('bytes', []);
					return;
				}

				value?.blob.arrayBuffer()
					.then((buffer) => {
						const byteArray = new Uint8Array(buffer);
						formikBag.setFieldValue('bytes', Array.from(byteArray));
						formikBag.setFieldValue('chatMessageType', ChatMessageType.VideoMessage);
					});
			}}
			onReset={() => {
				formikBag.setFieldValue('bytes', []);
			}}
		/>
	</React.Fragment>
};

export enum ChatFormTag {
	Voice,
	Video
}

export const ChatFormTagDictionary = {
	[ChatFormTag.Voice]: (formikBag: FormikProps<NewMessage>, {state, setState, waveColor}) =>
		state.recordAudioResult && <Tag
			className="chat-file-tag chat-audio-tag"
			title="Voice Message"
		>
			<VoiceMessageRecorder
				shape="default"
				waveColor={waveColor}
				className="btn-default"
				recordAudioResult={state.recordAudioResult}
				setRecordAudioResult={recordAudioResult => setState(prev => ({...prev, recordAudioResult}))}
				onChange={(value) => {
					setState(prev => ({...prev, recordVideoResult: undefined}));

					if (!value) {
						formikBag.setFieldValue('bytes', []);
						return;
					}

					value?.blob.arrayBuffer()
						.then((buffer) => {
							const byteArray = new Uint8Array(buffer);
							formikBag.setFieldValue('bytes', Array.from(byteArray));
							formikBag.setFieldValue('chatMessageType', ChatMessageType.VoiceMessage);
						});
				}}
			/>
		</Tag>,
	[ChatFormTag.Video]: (formikBag: FormikProps<NewMessage>, {state, setState, waveColor}) => state.recordVideoResult &&
		<Tag
			className="chat-file-tag chat-video-tag"
			title="Video Message"
		>
			<VideoMessageInput
				className="btn-default"
				containerClassName=" "
				waveColor={waveColor}
				recordVideoResult={state.recordVideoResult}
				setRecordVideoResult={recordVideoResult => setState(prev => ({...prev, recordVideoResult}))}
				onStop={(value) => {
					setState(prev => ({...prev, recordAudioResult: undefined}));

					if (!value) {
						formikBag.setFieldValue('bytes', []);
						return;
					}

					value?.blob.arrayBuffer()
						.then((buffer) => {
							const byteArray = new Uint8Array(buffer);
							formikBag.setFieldValue('bytes', Array.from(byteArray));
							formikBag.setFieldValue('chatMessageType', ChatMessageType.VideoMessage);
						});
				}}
				onReset={() => {
					formikBag.setFieldValue('bytes', []);
				}}
			/>
		</Tag>
};

type ChatFormButtonsWrappers = {
	[key in ChatFormButtons]?: (children) => React.ReactNode;
};

type ChatFormButtonsComponents = ChatFormButtons | ((formikBag: FormikProps<NewMessage>, props: ChatFormButtonsProps) => React.ReactNode);

export enum ChatHeaderButtons {
	Voice,
	Video,
	Mail
}

export const ChatHeaderButtonsDictionary = {
	[ChatHeaderButtons.Voice]: ({currentChat, support}) => <>
		<SystemCallButton
			roomCreateActor={support ? 'videoChatSupport' : 'videoChat'}
			roomId={`${currentChat.id}_${currentChat.name}`}
			roomName={currentChat.name}
			chatId={currentChat.id}
			button={(onClick, isDisabled) =>
				<button
					className="btn btn-sm btn-primary mr10"
					onClick={onClick}
					disabled={isDisabled}
				>
					<i className="fa fa-phone-square"/>
				</button>}
		/>
	</>,
	[ChatHeaderButtons.Video]: ({currentChat, support}) => <VideoChatButton
		roomCreateActor={support ? 'videoChatSupport' : 'videoChat'}
		support={support}
		roomId={`${currentChat.id}_${currentChat.name}`}
		roomName={currentChat.name}
		chatId={currentChat.id}
		notifyUserIds={support ? undefined : currentChat.contactsIds}
		button={(onClick, isDisabled) =>
			<button
				className="btn btn-sm btn-primary"
				onClick={onClick}
				disabled={isDisabled}
			>
				<i className="fa fa-video-camera"/>
			</button>}
	/>,
	[ChatHeaderButtons.Mail]: () => <></>
};

export enum ChatComponents {
	Mentions,
	Typing,
	LinkPreview,
	LinkPreviewGroup,
	ImageColorBox
}

export const ChatFormComponentsDictionary = {
	[ChatComponents.Mentions]: (props: CustomMentionProps) => <MentionsWithTyping {...props}/>,
	[ChatComponents.Typing]: ({chat, render, delay}) => <TypingComponent chat={chat} render={render} delay={delay}/>
};

export const ChatListComponentsDictionary = {
	[ChatComponents.Typing]: ({chat, delay}) => <>
		<TypingComponent chat={chat} delay={delay} render={(contacts) => {
			const res = contacts.map(contact => `${contact.firstName} ${contact.lastName}`).join(', ');
			const text = `${res} ${contacts.length > 1 ? ' are typing' : ' is typing'}`;

			return <div className="tag-chat__typings-container">
				{!!contacts.length && <div className="tag-chat__typing" title={text}>
					{text}
				</div>}
			</div>;
		}}/>
	</>
};

export const ChatMessageListComponentsDictionary = {
	[ChatComponents.LinkPreview]: previewInfo => <LinkPreviewComponent previewInfo={previewInfo} />,
	[ChatComponents.LinkPreviewGroup]: (links, linkPreviewId) => <LinkPreviewGroup links={links} linkPreviewId={linkPreviewId}/>,
	[ChatComponents.ImageColorBox]: ({defaultIdx, files, setImg}) =>
		<Colorbox
			files={files ? files : []}
			defaultIdx={defaultIdx ?? null}
			onCancel={() => setImg(undefined)}
		/>
};

export const ChatMessageTypeDictionary = {
	[ChatMessageType.Regular]: ({message, contacts, onImageClick, withRemoteId}) =>
		<RegularChatMessage message={message} onImageClick={onImageClick} contacts={contacts} />,
	[ChatMessageType.Email]: ({message, withRemoteId}) => <div style={{
		display: 'flex',
		alignItems: 'center',
		padding: '1rem',
		borderRadius: '0.5rem',
		backgroundColor: 'aliceblue',
		width: 'fit-content'
	}}>
		<i className="fa fa-envelope" style={{color: 'dodgerblue', fontSize: '2rem'}}/>
		<div style={{
			fontSize: '1.5rem',
			fontWeight: 'bold',
			marginLeft: '1rem',
			width: 'fit-content'
		}}>
			{message.text}
		</div>
	</div>,
	[ChatMessageType.PhoneCall]: ({message, withRemoteId}) => <div
		style={{
			display: 'flex',
			alignItems: 'center',
			padding: '1rem',
			borderRadius: '0.5rem',
			backgroundColor: '#effaef',
			width: 'fit-content'
		}}
	>
		<i className="fa fa-phone" style={{color: 'green', fontSize: '2rem'}}/>
		<div
			style={{
				fontSize: '1.5rem',
				fontWeight: 'bold',
				marginLeft: '1rem',
				width: 'fit-content'
			}}>
			{withRemoteId ? phoneFormat(message.text) : 'Operator called to your phone'}
		</div>
	</div>,
	[ChatMessageType.VideoChat]: ({message, withRemoteId, lastVideoCallId}) => <>
		<div className="chat-message-list-component__item-call-button">
			<VideoChatButton
				roomId={message.text}
				roomName={withRemoteId ? message.chat?.name ?? 'Unknown' : message.text.split('_')[1]}
				roomCreateActor={withRemoteId ? 'videoChatTuna' : 'videoChat'}
				button={(handleClick, isDisabled) =>
					<Button
						htmlType="button"
						shape="circle"
						onClick={handleClick}
						disabled={lastVideoCallId !== message.id || isDisabled}
					>
						<i className="fa fa-video-camera"/>
					</Button>}
			/>
		</div>
		<div className="chat-message-list-component__item-call-button">
			<SystemCallButton
				roomId={message.text}
				roomName={withRemoteId ? message.chat?.name ?? 'Unknown' : message.text.split('_')[1]}
				roomCreateActor={withRemoteId ? 'videoChatTuna' : 'videoChat'}
				button={(handleClick, isChatting) => <Button
					htmlType="button"
					shape="circle"
					onClick={handleClick}
					disabled={lastVideoCallId !== message.id || isChatting}
				>
					<i className="fa fa-phone-square"/>
				</Button>}
			/>
		</div>
	</>,
	[ChatMessageType.VoiceMessage]: ({message, contacts, withRemoteId, onMouseEnter}) => <div>
		<VoiceMessageChatPlayer
			voiceMessageUrl={message.files.find(f => f.file.name === 'voice_message.mp3') ?
				`${withRemoteId ? '/remote' : ''}/${message.files.find(f => f.file.name === 'voice_message.mp3')?.file.src}` : undefined}
			onPlayed={() => onMouseEnter && onMouseEnter(message)}
		/>
		<div dangerouslySetInnerHTML={{ __html: transformMentionMessage(getChatMessageMarkup(message.text), contacts, true) }} />
	</div>,
	[ChatMessageType.VideoMessage]: ({message, contacts, withRemoteId, onMouseEnter}) => <div>
		<VideoMessageChatPlayer
			videoMessageUrl={message.files.find(f => f.file.name === 'video_message.webm') ?
				`${withRemoteId ? '/remote' : ''}/${message.files.find(f => f.file.name === 'video_message.webm')?.file.src}` : undefined}
		/>
		<div dangerouslySetInnerHTML={{ __html: transformMentionMessage(getChatMessageMarkup(message.text), contacts, true) }} />
	</div>
};

export enum ChatMessageActions {
	Edit,
	Remove,
	Reaction
}

export const ChatMessageActionsDictionary = {
	[ChatMessageActions.Edit]: ({edit, message, isEdit, fromUser}) =>
		fromUser ? <button
			key="edit"
			className="message-action btn btn-default btn-sm"
			type="button"
			onClick={() => edit(isEdit ? null : message)}
		>
			{isEdit ? 'Cancel' : 'Edit'}
		</button> : null,
	[ChatMessageActions.Remove]: ({remove, isEdit, message, update, fromUser}) =>
		fromUser && !isEdit ? <button
			key="remove"
			className="message-action btn btn-danger btn-sm"
			type="button"
			onClick={remove}
		>
			Remove
		</button> : null,
};

export type ChatMessageActionsComponent = ChatMessageActions
	| (({remove, update, edit, isEdit, message, fromUser}) => React.ReactNode);

type ChatOtherComponents = ChatComponents | ((chat: Chat) => React.ReactNode);

export interface ChatSettingsChatPageSettings {
	path: string;
	chatIdUrlKey: string;
	archive: string;
}

export interface ChatSettingsProviderProps {
	children: any;
	requests?: ChatSettingsRequests;
	notificationTypes?: ChatSettingsNotificationTypes;
	userSettings?: ChatSettingsUserSettings;
	avatarSettings?: ChatSettingsAvatarSettings;
	formSettings?: ChatSettingsFormSettings;
	formButtons?: ChatFormButtonsComponents[];
	formTags?: ChatFormTag[];
	headerButtons?: ChatHeaderButtons[];
	messageTypes?: ChatMessageType[];
	otherComponents?: ChatOtherComponents[];
	pageSettings?: ChatSettingsChatPageSettings;
	chatFormButtonsWrappers?: ChatFormButtonsWrappers;
	chatStoreSettings?: ChatStoreSettings;
	title?: string;
	withRemoteId?: boolean;
	chatsFilters?: BaseParams;
	typingDelay?: number; // ms
	chatListHeaderSettings?: ChatListHeaderSettingsType;
	renderChatName?: ChatNameType;
	messagesHeaderComponents?: ChatMessagesHeaderSettingsType;
	notificationHideDelay?: number;
	maxChatMessageNotificationCount?: number;
	messageActions?: ChatMessageActionsComponent[];
	removeMessageConfirmation?: boolean;
}

export interface ChatSettingsProviderContextState {
	requests: ChatSettingsRequests;
	notificationTypes: ChatSettingsNotificationTypes;
	formSettings: ChatSettingsFormSettings;
	formButtons: ChatFormButtonsComponents[];
	otherComponents: ChatOtherComponents[];
	chatFormButtonsWrappers: ChatFormButtonsWrappers;
	pageSettings: ChatSettingsChatPageSettings;
	chatStoreSettings: ChatStoreSettings;
	title: string;
	avatarSettings: ChatSettingsAvatarSettings;
	formTags: ChatFormTag[];
	withRemoteId: boolean;
	typingDelay: number;
	chatListHeaderSettings: ChatListHeaderSettingsType;
	messagesHeaderComponents: ChatMessagesHeaderSettingsType;
	notificationHideDelay: number;
	maxChatMessageNotificationCount: number;
	messageActions?: ChatMessageActionsComponent[];
	chatsFilters?: BaseParams;
	renderChatName?: ChatNameType;
	removeMessageConfirmation?: boolean;
}

export interface ChatSettingsProviderContext {
	state: ChatSettingsProviderContextState;
	actions: any;
}

export interface ChatStoreSettings {
	getActionCreators: any;
	getChatStore: (store) => ({chats: List<Chat>, chatsLoaded: boolean});
	getMessages: (chatId) => (state) => (List<ChatMessage> | undefined);
	getCurrentChat: (state) => Chat | null;
	userUnviewedMessagesCountName: string;
}

export const createChatSettingsProviderContext = once(() => React.createContext({} as ChatSettingsProviderContext));

export const useChatSettingsProviderContext: () => ChatSettingsProviderContext = () =>
	React.useContext(createChatSettingsProviderContext());

export const ChatSettingsProvider: (p: ChatSettingsProviderProps) => React.ReactElement = (
	{
		children,
		requests = {
			chat: 'chat',
			chatUser: 'chatUser',
			chatMessage: 'chatMessage',
			chatMessageAccess: 'chatMessageAccess',
			typing: 'typing',
			completeChat: 'chatArchive',
			removeMessage: 'deleteChatMessage',
			updateMessage: 'changeChatMessage'
		},
		notificationTypes = {
			chat: 'Chat',
			chatUser: 'ChatUser',
			chatMessage: 'ChatMessage',
			chatMessageAccess: 'ChatMessageAccess',
			typing: 'Typing'
		},
		formSettings = {
			sendByEnter: false,
			underFormLabel: '',
			allowPasteImages: false,
			maxAttachmentsCount: 5,
			maxMessageLength: 500
		},
		formButtons = [
			ChatFormButtons.Video,
			ChatFormButtons.Voice,
			ChatFormButtons.File,
			ChatFormButtons.Emoji
		],
		pageSettings = {
			path: '/chats',
			chatIdUrlKey: 'chatId',
			archive: 'archive',
		},
		chatFormButtonsWrappers = {},
		otherComponents = [
			ChatComponents.Mentions,
			ChatComponents.Typing,
			ChatComponents.ImageColorBox
		],
		messageActions = [],
		avatarSettings = {
			getUserAvatar: user => <div
				className="chat-message-list-component__item-avatar"
				style={{backgroundImage: `url(${getAvatar(user)})`}}/>,
			getChatAvatar: getUserPick
		},
		chatStoreSettings = {
			getActionCreators: ChatsState.getActionCreators,
			getChatStore: state => ({chats: state.chats.chats, chatsLoaded: state.chats.chatsLoaded}),
			getMessages: chatId => state => state.chats.messages[chatId] || undefined,
			getCurrentChat: state => state.chats.currentChat,
			userUnviewedMessagesCountName: 'unviewedMessagesCount'
		},
		title = 'Chats',
		formTags = [ChatFormTag.Video, ChatFormTag.Voice],
		renderChatName = undefined,
		messagesHeaderComponents = [],
		withRemoteId = false,
		chatsFilters,
		typingDelay = 5000,
		chatListHeaderSettings = [],
		notificationHideDelay = 5000,
		maxChatMessageNotificationCount = 5,
		removeMessageConfirmation = true
	}
) => {
	const ChatSettingsProviderContext = createChatSettingsProviderContext();

	const setChatsFilters = (newFilters) => {
		setValue(value => ({
			state: {
				...value.state,
				chatsFilters: {
					...value.state.chatsFilters,
					...newFilters
				}},
			actions: value.actions
		}));
	};

	const [value, setValue] = React.useState({
		state: {
			requests,
			notificationTypes,
			formSettings,
			formButtons,
			chatFormButtonsWrappers,
			otherComponents,
			pageSettings,
			avatarSettings,
			chatStoreSettings,
			title,
			formTags,
			renderChatName,
			withRemoteId,
			chatsFilters,
			typingDelay,
			chatListHeaderSettings,
			messagesHeaderComponents,
			notificationHideDelay,
			maxChatMessageNotificationCount,
			messageActions,
			removeMessageConfirmation
		},
		actions: {
			setChatsFilters
		}
	});

	return (
		<ChatSettingsProviderContext.Provider value={value}>
			{children}
		</ChatSettingsProviderContext.Provider>
	);
};